/**
 * Magic-link Review Invitations & Responses
 *
 * Generalizes the existing 360Invite pattern so that ALL reviewer paths
 * (self, line manager, peer, direct report) use a single token-based flow.
 * No login required for the reviewer surface.
 *
 * Reviewer identity is resolved from the M365-synced employee directory by email.
 * Reviewers without an account in the directory can still submit — the token
 * itself authorises the write.
 */

import type { Timestamp } from 'firebase/firestore';
import type { OpticsLens } from './optics2026';

export type ReviewerRole = 'self' | 'line_manager' | 'peer' | 'direct_report';

export const REVIEWER_ROLE_LABELS: Record<ReviewerRole, string> = {
  self:          'Self',
  line_manager:  'Line Manager',
  peer:          'Peer',
  direct_report: 'Direct Report',
};

/**
 * Whether a reviewer's identity is shown to admin/reviewee or kept anonymous
 * in aggregated output. Self and line_manager are attributed; peer and
 * direct_report are anonymised in admin views.
 */
export const REVIEWER_ATTRIBUTION: Record<ReviewerRole, 'attributed' | 'anonymous'> = {
  self:          'attributed',
  line_manager:  'attributed',
  peer:          'anonymous',
  direct_report: 'anonymous',
};

export type InvitationStatus = 'sent' | 'opened' | 'in_progress' | 'submitted' | 'expired';

export type ReminderTag = 'h-3' | 'h-1' | 'h+1';

export interface ReviewInvitation {
  id: string;
  reviewFormId: string;
  cycleId: string;

  revieweeId: string;
  revieweeName: string;

  reviewerRole: ReviewerRole;
  reviewerId?: string;        // EPRUser.id if reviewer is in the directory; optional for ad-hoc
  reviewerEmail: string;
  reviewerName?: string;
  reviewerWaNumber?: string;

  token: string;              // single-use random hex (24 bytes)
  status: InvitationStatus;

  sentAt: Timestamp;
  expiresAt: Timestamp;
  openedAt?: Timestamp;
  submittedAt?: Timestamp;

  remindersSent: ReminderTag[];
}

/** A single area's score from one reviewer. */
export interface ReviewResponseEntry {
  areaId: string;                          // "O-A", "O-B", etc.
  scores: Partial<Record<OpticsLens, number>>;  // { ind: 4, team: 3, co: 5 } — partial allows skipped lenses
  evidence?: string;
  developmentAction?: string;
}

/** Self-only narrative — Section 7 of the PDF form. Rendered only when reviewerRole === 'self'. */
export interface SelfNarrative {
  deliveredIndividually?: string;
  supportedTeam?: string;
  contributedToCompany?: string;
  needSupport?: string;
}

export interface ReviewResponse {
  id: string;
  invitationId: string;
  reviewFormId: string;
  cycleId: string;

  revieweeId: string;
  reviewerRole: ReviewerRole;
  reviewerId?: string;        // omitted in admin queries when role attribution is 'anonymous'
  reviewerEmail?: string;     // kept for attributed roles only

  entries: ReviewResponseEntry[];
  selfNarrative?: SelfNarrative;

  submittedAt: Timestamp;
  createdAt: Timestamp;
}

/**
 * Lightweight directory entry used when HR builds a reviewer panel.
 * Sourced from M365-synced /users collection via account-portal IAM.
 */
export interface DirectoryEmployee {
  id: string;
  name: string;
  email: string;
  jobTitle?: string;
  unitId?: string;
  unitName?: string;
  directManagerId?: string;
  level?: 'general' | 'supervisor' | 'dept_head';
  isActive: boolean;
}
